import React, { useEffect, useCallback, useState } from "react";
import { InputText } from "primereact/inputtext";
import { Calendar } from "primereact/calendar";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { Dropdown } from "primereact/dropdown";
import { MultiSelect } from "primereact/multiselect";
import { Toast } from "primereact/toast";
import { Accordion, AccordionTab } from "primereact/accordion";
import TeacherAssignmentsDisplay from "../../../components/Teachers/TeacherAssignmentsDisplay";
import AssignSubjectsModal from "../../../components/Teachers/AssignSubjectsModal";
import { getTeacherAssignments } from "../../../api/teacherAssignmentApi";
import { getSessions } from "../../../api/sessionApi";
import { getGrades } from "../../../api/gradeApi";
import { getCurriculumSubjectsByGrade } from "../../../api/curriculumSubjectsApi";
import { bulkAssignSubjects } from "../../../api/teacherAssignmentApi";
import { getDepartments, addDepartment, deleteDepartment } from "../../../api/departmentApi";

// Update nested user state at a dotted path
const setNested = (setUser, path, value) => {
  setUser((prev) => {
    const next = { ...prev };
    const keys = path.split(".");
    let ref = next;
    for (let i = 0; i < keys.length - 1; i++) {
      const k = keys[i];
      ref[k] = ref[k] ? { ...ref[k] } : {};
      ref = ref[k];
    }
    ref[keys[keys.length - 1]] = value;
    return next;
  });
};

// Generic list helpers
const updateListItem = (user, setUser, listKey, idx, patch) => {
  const list = [...(user[listKey] || [])];
  list[idx] = { ...(list[idx] || {}), ...patch };
  setNested(setUser, listKey, list);
};

const removeListItem = (user, setUser, listKey, idx) => {
  const list = [...(user[listKey] || [])];
  list.splice(idx, 1);
  setNested(setUser, listKey, list);
};

const pushListItem = (user, setUser, listKey, item) => {
  const list = [...(user[listKey] || [])];
  list.push(item);
  setNested(setUser, listKey, list);
};

// Reusable FormField component for the main details
const FormField = ({ id, label, value, onChange, placeholder }) => (
  <div className="p-field mb-2 flex flex-column gap-2">
    <label htmlFor={id} className="font-bold text-md text-gray-700">
      {label}
    </label>
    <InputText
      id={id}
      name={id}
      value={value || ""}
      onChange={onChange}
      placeholder={placeholder}
      className="w-full border-none border-bottom-1 border-gray-300"
    />
  </div>
);

// Reusable component for a single item in a dynamic list
const DynamicListItem = ({ item, fields, onUpdate, onRemove, type = "default", extraData = {} }) => {
  if (type === "subjectAssignment") {
    const { sessions = [], grades = [], subjectsByGrade = {}, isEditing = false } = extraData;
    
    const handleGradeChange = (gradeId) => {
      onUpdate({ grade_id: gradeId, subject_ids: [] });
    };

    const handleSessionChange = (sessionId) => {
      onUpdate({ session_id: sessionId });
    };

    const handleSubjectsChange = (subjectIds) => {
      onUpdate({ subject_ids: subjectIds });
    };

    const selectedGrade = grades.find(g => g.id === item.grade_id);
    const selectedSession = sessions.find(s => s.id === item.session_id);
    const availableSubjects = selectedGrade ? subjectsByGrade[selectedGrade.id] || [] : [];

    return (
      <div className="grid align-items-center mb-3 p-3 border-1 border-gray-300 border-round" style={{ backgroundColor: 'var(--app-hover-bg)' }}>
        <div className="col-12 md:col-3">
          <label className="block text-sm font-medium text-gray-700 mb-1">Select Session</label>
          <Dropdown
            value={item.session_id}
            options={sessions}
            onChange={(e) => handleSessionChange(e.value)}
            optionLabel="name"
            optionValue="id"
            placeholder="Select Session"
            className="w-full border-gray-300"
          />
        </div>
        <div className="col-12 md:col-3">
          <label className="block text-sm font-medium text-gray-700 mb-1">Select Grade</label>
          <Dropdown
            value={item.grade_id}
            options={grades}
            onChange={(e) => handleGradeChange(e.value)}
            optionLabel="name"
            optionValue="id"
            placeholder="Select Grade"
            className="w-full border-gray-300"
          />
        </div>
        <div className="col-12 md:col-5">
          <label className="block text-sm font-medium text-gray-700 mb-1">Select Subjects</label>
          <MultiSelect
            value={item.subject_ids || []}
            options={availableSubjects}
            onChange={(e) => handleSubjectsChange(e.value)}
            optionLabel="name"
            optionValue="id"
            placeholder="Select Subjects"
            disabled={!item.grade_id}
            display="chip"
            className="w-full border-gray-300"
            filter
          />
          {!item.grade_id && (
            <small className="text-gray-500 text-sm mt-1 block">Please select a grade first</small>
          )}
        </div>
        <div className="col-12 md:col-1 flex justify-content-end align-items-end">
          {!isEditing && (
            <Button
              icon="pi pi-times"
              className="p-button-rounded p-button-text p-button-danger"
              onClick={onRemove}
              aria-label="Remove"
              style={{ color: '#ef4444' }}
            />
          )}
        </div>
      </div>
    );
  }

  // Default type (for qualifications, work history, certifications)
  return (
    <div className="grid align-items-center">
      {fields.map(({ name, placeholder, type = "text", colClass }) => (
        <div key={name} className={colClass}>
          {type === "text" && (
            <InputText
              placeholder={placeholder}
              value={item[name] || ""}
              onChange={(e) => onUpdate({ [name]: e.target.value })}
              className=" border-none border-bottom-1 border-gray-300"
            />
          )}
          {type === "date" && (
            <Calendar
              value={item[name] ? new Date(item[name]) : null}
              placeholder={placeholder}
              onChange={(e) => onUpdate({ [name]: e.value })}
              className=" border-none border-bottom-1 border-gray-300"
            />
          )}
          {type === "file" && (
            <>
              <input
                type="file"
                accept="image/*,application/pdf"
                onChange={(e) => {
                  const file = e.target.files && e.target.files[0];
                  if (file) onUpdate({ _file: file });
                }}
              />
              {item.certificate_document && !item._file && (
                <small className="text-600">
                  Existing: {item.certificate_document.split("/").pop()}
                </small>
              )}
            </>
          )}
        </div>
      ))}
      <div className="md:col-2 flex align-items-end px-3 justify-content-end">
        <div
          onClick={onRemove}
          aria-label="Remove"
          className="text-lg cursor-pointer flex align-items-center justify-content-center text-white bg-red-400"
          style={{ width: "20px", height: "20px", borderRadius: "100%" }}
          title="Remove"
        >
          -
        </div>
      </div>
    </div>
  );
};

// Reusable component for a dynamic list section
const DynamicList = ({
  title,
  listKey,
  items,
  setUser,
  fields,
  newItem,
  addLabel,
  type = "default",
  extraData = {},
  hasExistingData = false,
  onSave = null
}) => {
  const updateItem = useCallback(
    (idx, patch) => {
      const list = [...(items || [])];
      list[idx] = { ...(list[idx] || {}), ...patch };
      setNested(setUser, listKey, list);
    },
    [items, listKey, setUser]
  );

  const removeItem = useCallback(
    (idx) => {
      const list = [...(items || [])];
      list.splice(idx, 1);
      setNested(setUser, listKey, list);
    },
    [items, listKey, setUser]
  );

  const addItem = useCallback(() => {
    const list = [...(items || [])];
    list.push(newItem);
    setNested(setUser, listKey, list);
  }, [items, listKey, newItem, setUser]);

  return (
    <div className="mt-3">
      <div className="flex justify-content-between align-items-center mb-3">
        <h3 className="text-xl font-semibold text-gray-800">{title}</h3>
        {type === "subjectAssignment" && !hasExistingData && onSave && (
          <Button
            label="Save Assignments"
            icon="pi pi-save"
            className="p-button-sm"
            style={{ backgroundColor: 'var(--primary-color)', borderColor: 'var(--primary-color)' }}
            onClick={onSave}
          />
        )}
      </div>
      {(items || []).map((item, idx) => (
        <DynamicListItem
          key={idx}
          item={item}
          fields={fields}
          onUpdate={(patch) => updateItem(idx, patch)}
          onRemove={() => removeItem(idx)}
          type={type}
          extraData={{...extraData, isEditing: hasExistingData}}
        />
      ))}
      {!hasExistingData && (
        <div className="mt-3 flex justify-content-end">
          <Button
            label={addLabel}
            icon="pi pi-plus"
            className="p-button-outlined p-button-sm"
            onClick={addItem}
            style={{ borderColor: 'var(--primary-color)', color: 'var(--primary-color)' }}
          />
        </div>
      )}
    </div>
  );
};

const TeacherDetails = ({ user, setUser }) => {
  const [isAssignOpen, setIsAssignOpen] = useState(false);
  const [isViewAllOpen, setIsViewAllOpen] = useState(false);
  const [sessions, setSessions] = useState([]);
  const [activeSessions, setActiveSessions] = useState([]);
  const [grades, setGrades] = useState([]);
  const [subjectsByGrade, setSubjectsByGrade] = useState({});
  const [loadingSubjects, setLoadingSubjects] = useState(false);
  const [savingAssignments, setSavingAssignments] = useState(false);
  const [departments, setDepartments] = useState([]);
  const [isManageDeptOpen, setIsManageDeptOpen] = useState(false);
  const [newDeptName, setNewDeptName] = useState("");
  const toast = React.useRef(null);

  // Create mode = teacher profile doesn't exist yet (no id to attach assignments to)
  const isCreateMode = !user?.teacher?.id;

  const loadDepartments = useCallback(async () => {
    try {
      setDepartments(await getDepartments());
    } catch (e) {
      console.error("Error fetching departments:", e);
    }
  }, []);

  useEffect(() => {
    loadDepartments();
  }, [loadDepartments]);

  const handleAddDepartment = async () => {
    const name = newDeptName.trim();
    if (!name) return;
    try {
      await addDepartment(name);
      setNewDeptName("");
      await loadDepartments();
    } catch (e) {
      showToast("error", "Error", e.response?.data?.message || "Failed to add department");
    }
  };

  const handleDeleteDepartment = async (id) => {
    try {
      await deleteDepartment(id);
      await loadDepartments();
    } catch (e) {
      showToast("error", "Error", e.response?.data?.message || "Failed to delete department");
    }
  };

  const onFieldChange = (e) => {
    const { name, value } = e.target;
    setNested(setUser, `teacher.${name}`, value);
  };

  // Fetch sessions and grades on component mount
  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        const sessionsData = await getSessions();
        const data = Array.isArray(sessionsData) ? sessionsData : sessionsData?.data || [];
        
        // Filter active sessions (same logic as FeeVoucherPage)
        const activeSessionsData = data.filter(
          (s) => s.status?.toLowerCase() === "active" || s.is_active
        );
        
        setSessions(data);
        setActiveSessions(activeSessionsData);
        
        // Get grades if we have an active session
        if (activeSessionsData.length > 0) {
          const gradesData = await getGrades(activeSessionsData[0].id);
          const gradeList = Array.isArray(gradesData) ? gradesData : gradesData?.data || [];
          setGrades(gradeList);
        }
      } catch (error) {
        console.error("Error fetching initial data:", error);
      }
    };
    fetchInitialData();
  }, []);

  // Fetch subjects for a specific grade
  const fetchSubjectsForGrade = useCallback(async (gradeId) => {
    if (!gradeId || subjectsByGrade[gradeId]) return;
    
    setLoadingSubjects(true);
    try {
      const curriculum = await getCurriculumSubjectsByGrade(gradeId);
      const subjectOptions = curriculum.data.map(cs => cs.subject);
      setSubjectsByGrade(prev => ({
        ...prev,
        [gradeId]: subjectOptions
      }));
    } catch (error) {
      console.error(`Error fetching subjects for grade ${gradeId}:`, error);
    } finally {
      setLoadingSubjects(false);
    }
  }, [subjectsByGrade]);

  // Pre-fetch subjects for existing assignments
  useEffect(() => {
    const assignments = user.subjectAssignments || [];
    assignments.forEach(assignment => {
      if (assignment.grade_id && !subjectsByGrade[assignment.grade_id]) {
        fetchSubjectsForGrade(assignment.grade_id);
      }
    });
  }, [user.subjectAssignments, fetchSubjectsForGrade, subjectsByGrade]);

  const handleSaveAssignment = (assignment) => {
    console.log("Saving assignment:", assignment);
    const newAssignments = [...(user.subjectAssignments || []), assignment];
    setNested(setUser, "subjectAssignments", newAssignments);
    console.log("Updated subjectAssignments in user data:", newAssignments);
    
    // Fetch subjects for the new grade if needed
    if (assignment.grade_id && !subjectsByGrade[assignment.grade_id]) {
      fetchSubjectsForGrade(assignment.grade_id);
    }
  };

  // Save subject assignments from the form (edit mode only — in create mode
  // assignments are submitted with the user create payload instead)
  const saveSubjectAssignments = async () => {
    if (!user?.teacher?.id) {
      showToast('info', 'Not saved yet', 'Assignments will be saved automatically when the teacher is created.');
      return;
    }

    const assignments = user.subjectAssignments || [];
    
    // Validate assignments
    const invalidAssignments = assignments.filter(
      assignment => !assignment.session_id || !assignment.grade_id || assignment.subject_ids.length === 0
    );
    
    if (invalidAssignments.length > 0) {
      showToast('error', 'Error', 'Please select session, grade, and at least one subject for all assignments');
      return;
    }

    setSavingAssignments(true);
    try {
      // Process each assignment individually
      const savePromises = assignments.map(async (assignment) => {
        if (!assignment.session_id || !assignment.grade_id) return;
        
        const payload = {
          teacher_id: user.teacher.id,
          session_id: assignment.session_id,
          grade_id: assignment.grade_id,
          subject_ids: assignment.subject_ids || []
        };

        await bulkAssignSubjects(payload);
      });

      await Promise.all(savePromises);
      
      showToast('success', 'Success', 'Subject assignments saved successfully');
      
      // Refresh assignments display
      await refreshAssignments();
      
      // Clear the form assignments after successful save
      setNested(setUser, "subjectAssignments", [
        { session_id: activeSessions.length > 0 ? activeSessions[0].id : null, grade_id: null, subject_ids: [] },
      ]);
      
    } catch (error) {
      console.error("Error saving subject assignments:", error);
      showToast('error', 'Error', error.response?.data?.message || 'Failed to save assignments');
    } finally {
      setSavingAssignments(false);
    }
  };

  const refreshAssignments = useCallback(async () => {
    if (!user?.teacher?.id) {
      console.log("No teacher ID available:", user?.teacher);
      return;
    }
    try {
      console.log("Refreshing assignments for teacher ID:", user.teacher.id);
      const data = await getTeacherAssignments(user.teacher.id);
      console.log("Fetched assignments data:", data);
      setNested(setUser, "teacherAssignments", data);
      console.log("Updated user state with new assignments");
    } catch (e) {
      console.error("Error refreshing assignments:", e);
      // ignore refresh errors in UI form context
    }
  }, [setUser, user?.teacher?.id]);

  // Check if teacher has existing assignments
  const hasExistingAssignments = user.teacherAssignments && user.teacherAssignments.length > 0;

  const showToast = (severity, summary, detail) => {
    if (toast.current) {
      toast.current.show({ severity, summary, detail, life: 3000 });
    }
  };

  // Initialize subjectAssignments with active session (only — no empty rows for other sections)
  useEffect(() => {
    if (!hasExistingAssignments && (user.subjectAssignments || []).length === 0 && activeSessions.length > 0) {
      setNested(setUser, "subjectAssignments", [
        { session_id: activeSessions[0].id, grade_id: null, subject_ids: [] },
      ]);
    }
  }, [user.subjectAssignments?.length, hasExistingAssignments, activeSessions, setUser]);

  const qualificationFields = [
    { name: "degree", placeholder: "Degree", colClass: "col-12 md:col-3" },
    {
      name: "institution",
      placeholder: "Institution",
      colClass: "col-12 md:col-3",
    },
    {
      name: "start_date",
      placeholder: "Start",
      type: "date",
      colClass: "col-12 md:col-2",
    },
    {
      name: "end_date",
      placeholder: "End",
      type: "date",
      colClass: "col-12 md:col-2",
    },
  ];

  const workHistoryFields = [
    { name: "school", placeholder: "School", colClass: "col-12 md:col-3" },
    { name: "subject", placeholder: "Subject", colClass: "col-12 md:col-3" },
    {
      name: "start_year",
      placeholder: "Start Year",
      colClass: "col-12 md:col-2",
    },
    { name: "end_year", placeholder: "End Year", colClass: "col-12 md:col-2" },
  ];

  const certificationFields = [
    { name: "title", placeholder: "Title", colClass: "col-12 md:col-4" },
    {
      name: "organization",
      placeholder: "Organization",
      colClass: "col-12 md:col-3",
    },
    { name: "certificate_document", type: "file", colClass: "col-12 md:col-3" },
  ];

  return (
    <div className="teacher-details-section w-full">
      <Toast ref={toast} />

      <Accordion multiple activeIndex={[0]}>

        {/* Section 1: Basic Info */}
        <AccordionTab header="Basic Info">
          <div className="grid">
            <div className="col-12 md:col-6">
              <FormField id="salary" label="Salary" value={user.teacher?.salary} onChange={onFieldChange} placeholder="e.g., 50000" />
            </div>
            <div className="col-12 md:col-6">
              <div className="p-field mb-2 flex flex-column gap-2">
                <label htmlFor="department" className="font-bold text-md text-gray-700">Department</label>
                <div className="flex gap-2 align-items-center">
                  <Dropdown
                    id="department"
                    value={user.teacher?.department || null}
                    options={
                      user.teacher?.department && !departments.some((d) => d.name === user.teacher.department)
                        ? [{ id: -1, name: user.teacher.department }, ...departments]
                        : departments
                    }
                    optionLabel="name"
                    optionValue="name"
                    onChange={(e) => setNested(setUser, "teacher.department", e.value)}
                    placeholder={departments.length ? "Select Department" : "No departments defined yet"}
                    showClear
                    filter
                    className="w-full"
                  />
                  <Button
                    icon="pi pi-cog"
                    className="p-button-outlined p-button-sm"
                    onClick={() => setIsManageDeptOpen(true)}
                    tooltip="Manage departments"
                    tooltipOptions={{ position: "top" }}
                  />
                </div>
              </div>
            </div>
            <div className="col-12 md:col-6">
              <FormField id="designation" label="Designation" value={user.teacher?.designation} onChange={onFieldChange} placeholder="e.g., Senior Teacher" />
            </div>
            <div className="col-12 md:col-6">
              <div className="p-field mb-2 flex flex-column gap-2">
                <label htmlFor="hire_date" className="font-bold text-md text-gray-700">Hire Date</label>
                <Calendar
                  id="hire_date"
                  value={user.teacher?.hire_date ? new Date(user.teacher.hire_date) : null}
                  onChange={(e) => setNested(setUser, "teacher.hire_date", e.value)}
                  dateFormat="yy-mm-dd"
                  showIcon
                  className="w-full"
                />
              </div>
            </div>
          </div>
        </AccordionTab>

        {/* Section 2: Qualifications */}
        <AccordionTab header="Qualifications">
          <DynamicList
            listKey="teacherQualifications"
            items={user.teacherQualifications}
            setUser={setUser}
            fields={qualificationFields}
            newItem={{ degree: "", institution: "", start_date: null, end_date: null }}
            addLabel="Add Qualification"
          />
        </AccordionTab>

        {/* Section 3: Work History */}
        <AccordionTab header="Work History">
          <DynamicList
            listKey="teacherWorkHistories"
            items={user.teacherWorkHistories}
            setUser={setUser}
            fields={workHistoryFields}
            newItem={{ school: "", subject: "", start_year: "", end_year: "" }}
            addLabel="Add Work History"
          />
        </AccordionTab>

        {/* Section 4: Certifications */}
        <AccordionTab header="Certifications">
          <DynamicList
            listKey="teacherCertifications"
            items={user.teacherCertifications}
            setUser={setUser}
            fields={certificationFields}
            newItem={{ title: "", organization: "", certificate_document: "", _file: null }}
            addLabel="Add Certification"
          />
        </AccordionTab>

        {/* Section 5: Subject Assignments */}
        <AccordionTab header={
          <div className="flex align-items-center justify-content-between w-full">
            <span>Subject Assignments</span>
            <div className="flex gap-2" onClick={(e) => e.stopPropagation()}>
              {!isCreateMode && (
                <Button
                  label="Assign"
                  icon="pi pi-plus"
                  className="p-button-sm p-button-outlined"
                  style={{ borderColor: 'var(--primary-color)', color: 'var(--primary-color)' }}
                  onClick={() => setIsAssignOpen(true)}
                />
              )}
              {hasExistingAssignments && (
                <Button
                  label="View All"
                  icon="pi pi-eye"
                  className="p-button-sm p-button-outlined"
                  style={{ borderColor: 'var(--app-text-secondary)', color: 'var(--app-text-secondary)' }}
                  onClick={() => setIsViewAllOpen(true)}
                />
              )}
            </div>
          </div>
        }>
          {hasExistingAssignments ? (
            <div>
              <p className="text-color-secondary mb-3 text-sm">
                Teacher has existing assignments. Use "Assign" to add more or "View All" to manage them.
              </p>
              {user.teacherAssignments && user.teacherAssignments.length > 0 && (
                <div className="grid">
                  {user.teacherAssignments.slice(0, 4).map((assignment, index) => (
                    <div key={assignment.id || index} className="col-12 md:col-6 mb-2">
                      <div className="border-1 border-round p-2 flex align-items-center gap-2" style={{ backgroundColor: 'var(--app-hover-bg)' }}>
                        <span className="font-bold" style={{ color: 'var(--primary-color)' }}>{index + 1}.</span>
                        <div>
                          <div className="font-semibold text-sm">{assignment.subject?.name}</div>
                          <div className="text-xs text-color-secondary">
                            {assignment.grade?.name} {assignment.session?.name && `| ${assignment.session.name}`}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <>
              <DynamicList
                listKey="subjectAssignments"
                items={user.subjectAssignments}
                setUser={setUser}
                fields={[]}
                newItem={{ session_id: activeSessions.length > 0 ? activeSessions[0].id : null, grade_id: null, subject_ids: [] }}
                addLabel="Add Assignment"
                type="subjectAssignment"
                extraData={{ sessions: activeSessions, grades, subjectsByGrade, loadingSubjects }}
              />
              {isCreateMode ? (
                <div className="mt-3 flex justify-content-end">
                  <small className="text-color-secondary">
                    <i className="pi pi-info-circle mr-1" />
                    Assignments will be saved automatically when you click Add.
                  </small>
                </div>
              ) : (
                <div className="mt-3 flex justify-content-end">
                  <Button
                    label="Save Assignments"
                    icon="pi pi-save"
                    className="p-button-sm"
                    style={{ backgroundColor: '#22c55e', borderColor: '#22c55e' }}
                    onClick={saveSubjectAssignments}
                    loading={savingAssignments}
                  />
                </div>
              )}
            </>
          )}
        </AccordionTab>

      </Accordion>

      {/* Manage Departments Dialog */}
      <Dialog
        header="Manage Departments"
        visible={isManageDeptOpen}
        style={{ width: "420px", maxWidth: "95vw" }}
        onHide={() => setIsManageDeptOpen(false)}
      >
        <div className="flex gap-2 mb-3">
          <InputText
            value={newDeptName}
            onChange={(e) => setNewDeptName(e.target.value)}
            placeholder="New department name"
            className="w-full"
            onKeyDown={(e) => e.key === "Enter" && handleAddDepartment()}
          />
          <Button icon="pi pi-plus" label="Add" className="p-button-sm" onClick={handleAddDepartment} />
        </div>
        {departments.length === 0 ? (
          <small className="text-color-secondary">No departments yet. Add the first one above.</small>
        ) : (
          departments.map((d) => (
            <div key={d.id} className="flex align-items-center justify-content-between py-2 border-bottom-1 border-gray-200">
              <span>{d.name}</span>
              <Button
                icon="pi pi-trash"
                className="p-button-rounded p-button-text p-button-danger p-button-sm"
                onClick={() => handleDeleteDepartment(d.id)}
                aria-label={`Delete ${d.name}`}
              />
            </div>
          ))
        )}
      </Dialog>

      {/* Assign Subjects Modal */}
      {isAssignOpen && (
        <AssignSubjectsModal
          visible={isAssignOpen}
          onHide={async () => { setIsAssignOpen(false); await refreshAssignments(); }}
          teacher={user?.teacher}
          onSaveAssignment={handleSaveAssignment}
          activeSessions={activeSessions}
        />
      )}

      {/* View All Dialog */}
      <Dialog
        header="All Assigned Grades & Subjects"
        visible={isViewAllOpen}
        style={{ width: "50vw" }}
        onHide={() => setIsViewAllOpen(false)}
      >
        <TeacherAssignmentsDisplay
          assignments={user.teacherAssignments}
          maxVisible={Number.POSITIVE_INFINITY}
          hideViewButtons
          teacher={user?.teacher}
          onAssignmentUpdate={refreshAssignments}
          viewOnly={true}
        />
      </Dialog>
    </div>
  );
};

export default TeacherDetails;